import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { Row, Col } from 'react-bootstrap';
import { toast } from 'react-toastify';
import useHandleSearch from 'utils/customHooks/useHandleSearch';
import { deleteHelpText } from 'api/company';
import { getHelpText } from 'api/pathForHook/company';
import { usePermissions } from 'components/backoffice/PermissionsContext';
import CustomTable from 'components/backoffice/customTable';
import DropdownIcons from 'components/backoffice/dropDownIcons';
import Title from 'components/backoffice/texts/title';
import Button from 'components/backoffice/buttonCustom';
import DeleteModalCustom from 'components/backoffice/modals/DeleteModalCustom';
import SearchTable from 'components/backoffice/table/SearchTable';

const HelpTextBackoffice = () => {
  const navigate = useNavigate();
  const userPermissions = usePermissions();
  const { t } = useTranslation(['backoffice']);
  const [idItem, setIdItem] = useState();
  const [showModalDelete, setShowModalDelete] = useState(false);
  const [page, setPage] = useState(1);
  const [inputValue, setInputValue] = useState('');
  const [responseInfo, setResponseInfo] = useState({});
  const { setTitle } = useOutletContext();

  const colsHelpText = ['ID', t('titulo'), t('actions')];
  const { fetchData, stateData, loading } = useHandleSearch();

  const getHelpTexts = async () => {
    try {
      const payload = {
        page,
        page_size: 10,
        search: inputValue,
        fields: 'id,title,slug'
      };
      await fetchData(getHelpText(), payload);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    setTitle(t('helpText'));
    return () => {
      setTitle('');
    };
  }, [setTitle]);

  useEffect(() => {
    const { results, errors, noResults, totalPages } = stateData;
    if (results !== null) {
      setResponseInfo({ results, errors, noResults, totalPages });
    } else {
      setResponseInfo({ errors });
    }
  }, [stateData]);

  useEffect(() => {
    getHelpTexts();
  }, [page, inputValue]);

  const handleShowModalDelete = (item, id, setOpenModal) => {
    if (item.id === id) {
      setOpenModal(true);
      setIdItem(id);
    }
  };

  const handleDelete = useCallback(async () => {
    const { status } = await deleteHelpText(idItem);
    if (status === 204) {
      toast.success(t('eliminadocorrectamente'));
      setInputValue('');
      getHelpTexts();
    }
    setShowModalDelete(false);
  }, [showModalDelete]);

  const handleEdit = (id) => navigate(`/intranet/help-text/edit/${id}`);
  const handleCreate = () => navigate(`/intranet/help-text/create`);

  const handleClear = () => {
    setInputValue('');
    setPage(1);
    setResponseInfo(prevResponseInfo => ({ ...prevResponseInfo, noResults: false }));
  };

  return (
    <div className="d-lg-grid">
      <Title text={t('helpText')} />
      <Row className="d-flex w-md-100 mt-3">
        <Col md={5} xs={12} className="d-flex text-start">
          <SearchTable
            value={inputValue}
            handleSearch={setInputValue}
            handleClear={handleClear}
            placeholder={t('searchByTitle')}
          />
        </Col>
        <Col md={7} xs={12} className="text-end mt-3 mt-md-0">
          {userPermissions.includes('editorial.__add') && (
            <Button color="primary" onClick={handleCreate}>
              {t('crear')}
            </Button>
          )}
        </Col>
      </Row>
      <div className="table-responsive">
        <CustomTable
          cols={colsHelpText}
          noResults={responseInfo?.noResults}
          isLoading={loading}
          page={page}
          changePage={setPage}
          totalPages={responseInfo?.totalPages}
        >
          {responseInfo?.results &&
            responseInfo.results.map((helpText, index) => {
              return (
                <tr key={helpText.id}>
                  <td className="text-light">{`#${helpText.id}`}</td>
                  <td className="text-light">{helpText.title || helpText.slug}</td>
                  <td className="text-light item-dropdown text-end">
                    <DropdownIcons
                      idItem={index}
                      handleEdit={userPermissions.includes('editorial.__modify') && (() => handleEdit(helpText.id))}
                      handleDelete={userPermissions.includes('editorial.__delete') && (() => handleShowModalDelete(helpText, helpText.id, setShowModalDelete))}
                    />
                  </td>
                </tr>
              );
            })}
        </CustomTable>
      </div>
      <DeleteModalCustom
        openModal={showModalDelete}
        setOpenModal={setShowModalDelete}
        handleDelete={handleDelete}
        deleteTitle={t('deleteHelpText')}
        beforeTitle={t('helpText')}
      />
    </div>
  );
};

export default HelpTextBackoffice;
